import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { medicationService } from '../services/medicationService'
import { prescriptionService } from '../services/prescriptionService'
import { formularyService, FormularyAlternativeReference } from '../services/formularyService'
import { MedicationReference } from '../types/medication'
import {
  Prescription,
  PrescriptionMedication,
  CreatePrescriptionInput,
  PrescriptionType,
  ScriptType,
} from '../types/prescription'
import { Patient } from '../types/patient'
import { getAgeDisplay, isUnder12 } from '../utils/ageUtils'
import { getAtcCodesForDrugIds } from '../utils/medicationAtcUtils'
import { buildMedicationOrderContext } from '../utils/buildMedicationOrderContext'
import { hasOpiateFamilyAtcCode } from '../utils/opiateMedicationUtils'
import { AllergyInteractionWarnings } from './AllergyInteractionWarnings'
import { MedicationAIInsightsPanel } from './MedicationAIInsightsPanel'
import { AlternativeMedications } from './AlternativeMedications'
import { ActivePregnancyPrescribingAlert } from './ActivePregnancyPrescribingAlert'
import { Modal } from './Modal'
import './PrescriptionBuilder.css'

interface PrescriptionBuilderProps {
  patientId: string
  patient?: Patient
  prescription?: Prescription
  onSave?: (prescription: Prescription) => void
  onCancel?: () => void
}

export function PrescriptionBuilder({
  patientId,
  patient,
  prescription,
  onSave,
  onCancel,
}: PrescriptionBuilderProps) {
  const queryClient = useQueryClient()
  const isEditing = !!prescription

  // Form state
  const [prescriptionType, setPrescriptionType] = useState<PrescriptionType>(
    prescription?.prescriptionType || 'gms',
  )
  const [scriptType, setScriptType] = useState<ScriptType>(prescription?.scriptType || 'acute')
  const [medications, setMedications] = useState<PrescriptionMedication[]>(
    prescription?.medications || [],
  )
  const [instructions, setInstructions] = useState(prescription?.instructions || '')
  const [isHandwritten, setIsHandwritten] = useState(prescription?.isHandwritten || false)
  const [handwrittenNotes, setHandwrittenNotes] = useState(prescription?.handwrittenNotes || '')
  const [reviewDate, setReviewDate] = useState(prescription?.reviewDate || '')
  const [maxRepeats, setMaxRepeats] = useState<number | undefined>(prescription?.maxRepeats)

  // Medication search
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<MedicationReference[]>([])
  const searchTimeoutRef = useRef<number | undefined>(undefined)

  // Allergy/interaction checking
  const [allergyCheck, setAllergyCheck] = useState<any>(null)
  const [interactionCheck, setInteractionCheck] = useState<any>(null)
  const [dosageValidation, setDosageValidation] = useState<any>(null)
  const [overrideWarnings, setOverrideWarnings] = useState<any>(null)
  const [formularyStatuses, setFormularyStatuses] = useState<Record<string, boolean>>({})
  const [formularyAlternativesForAi, setFormularyAlternativesForAi] = useState<FormularyAlternativeReference[]>([])
  const [isOpiateAlertOpen, setIsOpiateAlertOpen] = useState(false)
  const [hasShownOpiateAlert, setHasShownOpiateAlert] = useState(false)
  const [opiateAlertMedicationNames, setOpiateAlertMedicationNames] = useState<string[]>([])

  // Fetch patient if not provided
  const { data: patientData } = useQuery<Patient>({
    queryKey: ['patient', patientId],
    queryFn: async () => {
      // Use patientService if available, otherwise fetch directly
      const { patientService } = await import('../services/patientService')
      return patientService.getById(patientId)
    },
    enabled: !patient && !!patientId,
  })

  const currentPatient = patient || patientData

  const createMutation = useMutation({
    mutationFn: (payload: CreatePrescriptionInput) => prescriptionService.createPrescription(patientId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId] })
      onSave?.(data)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<CreatePrescriptionInput>) => {
      if (!prescription) throw new Error('No prescription to update')
      return prescriptionService.updatePrescription(prescription.prescriptionId, updates)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions', patientId] })
      onSave?.(data)
    },
  })

  // Medication search
  useEffect(() => {
    if (searchTimeoutRef.current !== undefined) {
      window.clearTimeout(searchTimeoutRef.current)
    }

    const term = searchTerm.trim()
    if (!term || term.length < 3) {
      setSearchResults([])
      return
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        const res = await medicationService.searchReference(term)
        setSearchResults(res.items)
      } catch (err) {
        console.error('Failed to search medications', err)
      }
    }, 300)
    searchTimeoutRef.current = timeoutId

    return () => {
      if (searchTimeoutRef.current !== undefined) {
        window.clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Check allergies and interactions when medications change
  useEffect(() => {
    if (medications.length === 0) {
      setAllergyCheck(null)
      setInteractionCheck(null)
      setDosageValidation(null)
      return
    }

    let cancelled = false

    const run = async () => {
      const atcByRef = await getAtcCodesForDrugIds(medications.map((m) => m.referenceDrugId))

      const medPayload = medications.map((m) => ({
        name: m.name,
        referenceDrugId: m.referenceDrugId,
        atcCodes: m.referenceDrugId ? atcByRef.get(m.referenceDrugId) || [] : [],
      }))

      const checkAllergies = async () => {
        try {
          const result = await prescriptionService.checkAllergies(patientId, medPayload)
          if (!cancelled) setAllergyCheck(result)
        } catch (err) {
          console.error('Failed to check allergies', err)
        }
      }

      const checkInteractions = async () => {
        try {
          const result = await prescriptionService.checkDrugInteractions(medPayload, patientId)
          if (!cancelled) setInteractionCheck(result)
        } catch (err) {
          console.error('Failed to check interactions', err)
        }
      }

      const checkDosages = async () => {
        try {
          const { dosageValidationService } = await import('../services/dosageValidationService')
          const result = await dosageValidationService.validateDosages({
            medications: medications.map((m) => ({
              name: m.name,
              referenceDrugId: m.referenceDrugId,
              dose: m.dose,
              indication: m.indication,
            })),
            patientId,
          })
          if (!cancelled) setDosageValidation(result)
        } catch (err) {
          console.error('Failed to validate dosages', err)
        }
      }

      await Promise.all([checkAllergies(), checkInteractions(), checkDosages()])
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [medications, patientId])

  // Formulary flags for prescription lines and for current search results (so dropdown shows badges before add)
  useEffect(() => {
    let isActive = true

    const fetchFormularyStatuses = async () => {
      const fromPrescription = medications.map((m) => m.referenceDrugId).filter(Boolean) as string[]
      const fromSearch = searchResults.map((m) => m.drugIdPK).filter(Boolean)
      const uniqueReferenceIds = Array.from(new Set([...fromPrescription, ...fromSearch]))

      const missingIds = uniqueReferenceIds.filter((id) => formularyStatuses[id] === undefined)
      if (missingIds.length === 0) {
        return
      }

      try {
        const results = await Promise.all(
          missingIds.map(async (id) => {
            const res = await formularyService.checkFormulary(id)
            return { id, inFormulary: res.inFormulary }
          }),
        )

        if (!isActive) return
        setFormularyStatuses((prev) => {
          const next = { ...prev }
          results.forEach((item) => {
            next[item.id] = item.inFormulary
          })
          return next
        })
      } catch (err) {
        console.error('Failed to check formulary status', err)
      }
    }

    void fetchFormularyStatuses()

    return () => {
      isActive = false
    }
  }, [medications, searchResults, formularyStatuses])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const med = medications.find((m) => m.referenceDrugId)
      if (!med?.referenceDrugId) {
        setFormularyAlternativesForAi([])
        return
      }
      try {
        const res = await formularyService.getFormularyAlternatives(med.referenceDrugId)
        if (!cancelled) setFormularyAlternativesForAi(res.alternatives)
      } catch {
        if (!cancelled) setFormularyAlternativesForAi([])
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [medications])

  useEffect(() => {
    if (hasShownOpiateAlert || medications.length === 0) {
      return
    }

    let cancelled = false
    const detectOpiateFamilyMeds = async () => {
      const referenceIds = medications
        .map((medication) => medication.referenceDrugId)
        .filter((referenceId): referenceId is string => Boolean(referenceId))

      if (referenceIds.length === 0) {
        return
      }

      try {
        const atcByRef = await getAtcCodesForDrugIds(referenceIds)
        const matchingMedicationNames = medications
          .filter(
            (medication) =>
              Boolean(medication.referenceDrugId) &&
              hasOpiateFamilyAtcCode(atcByRef.get(medication.referenceDrugId as string) || []),
          )
          .map((medication) => medication.name)

        if (!cancelled && matchingMedicationNames.length > 0) {
          setOpiateAlertMedicationNames(Array.from(new Set(matchingMedicationNames)))
          setIsOpiateAlertOpen(true)
          setHasShownOpiateAlert(true)
        }
      } catch (err) {
        console.error('Failed to detect opiate family medications', err)
      }
    }

    void detectOpiateFamilyMeds()

    return () => {
      cancelled = true
    }
  }, [medications, hasShownOpiateAlert])

  const handleAddMedication = (med: MedicationReference) => {
    // Check if medication is already added
    const alreadyAdded = medications.some(
      (m) => m.referenceDrugId === med.drugIdPK || m.name.toLowerCase() === med.productName.toLowerCase()
    )
    
    if (alreadyAdded) {
      alert('This medication is already added to the prescription')
      return
    }

    if (hasOpiateFamilyAtcCode(med.atcCodes || [])) {
      setOpiateAlertMedicationNames((currentNames) =>
        Array.from(new Set([...currentNames, med.productName])),
      )
      setIsOpiateAlertOpen(true)
      setHasShownOpiateAlert(true)
    }

    const newMed: PrescriptionMedication = {
      medicationId: `med-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      referenceDrugId: med.drugIdPK,
      name: med.productName,
      dose: med.strength || undefined,
      route: med.routesOfAdministration?.[0] || '',
      quantity: {
        value: med.packSize || 1,
        unit: med.dosageForm?.toLowerCase().includes('tablet') ? 'tablets' : 'units',
      },
      isGMSReimbursable: med.gmsReimbursable || false,
      gmsCode: med.gmsCodes?.[0],
    }

    setMedications([...medications, newMed])
    // Keep search term but clear results so user can search for next medication
    setSearchTerm('')
    setSearchResults([])
  }

  const handleRemoveMedication = (medicationId: string) => {
    setMedications(medications.filter((m) => m.medicationId !== medicationId))
  }

  const handleUpdateMedication = (medicationId: string, updates: Partial<PrescriptionMedication>) => {
    setMedications(
      medications.map((m) => (m.medicationId === medicationId ? { ...m, ...updates } : m)),
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (medications.length === 0) {
      alert('Please add at least one medication')
      return
    }

    // Validate that override reasons are provided if warnings exist
    if (allergyCheck?.hasAllergies || interactionCheck?.hasInteractions) {
      const hasAllergyOverrides =
        !allergyCheck?.hasAllergies ||
        allergyCheck.allergies.every((a: { allergyId: string }) => 
          overrideWarnings?.allergies?.some((o: { allergyId: string; reason: string }) => o.allergyId === a.allergyId && o.reason)
        )
      const hasInteractionOverrides =
        !interactionCheck?.hasInteractions ||
        (overrideWarnings?.interactions?.length || 0) >= (interactionCheck.interactions.length || 0)

      if (!hasAllergyOverrides || !hasInteractionOverrides) {
        alert('Please provide override reasons for all warnings before proceeding')
        return
      }
    }

    // Validate dosage override reasons
    if (dosageValidation?.hasWarnings) {
      const hasDosageOverrides =
        (overrideWarnings?.dosages?.length || 0) >= (dosageValidation.warnings.length || 0) &&
        dosageValidation.warnings.every((_: unknown, idx: number) => {
          const override = overrideWarnings?.dosages?.find((d: { warningId: string; reason: string }) => d.warningId === `warning-${idx}`)
          return override && override.reason
        })

      if (!hasDosageOverrides) {
        alert('Please provide override reasons for all dosage warnings before proceeding')
        return
      }
    }

    const payload: CreatePrescriptionInput = {
      patientId,
      prescriptionType,
      scriptType,
      medications,
      instructions: instructions || undefined,
      isHandwritten,
      handwrittenNotes: handwrittenNotes || undefined,
      reviewDate: reviewDate || undefined,
      maxRepeats: maxRepeats || undefined,
      overrideWarnings: overrideWarnings || undefined,
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(payload)
      } else {
        await createMutation.mutateAsync(payload)
      }
    } catch (err: any) {
      console.error('Failed to save prescription', err)
      alert(err?.response?.data?.message || 'Failed to save prescription')
    }
  }

  const patientAge = currentPatient ? getAgeDisplay(currentPatient.dateOfBirth) : ''
  const showAgeInMonths = currentPatient ? isUnder12(currentPatient.dateOfBirth) : false

  return (
    <div className="prescription-builder">
      <div className="prescription-builder-header">
        <h3>{isEditing ? 'Edit Prescription' : 'New Prescription'}</h3>
        {onCancel && (
          <button type="button" className="btn-link" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>

      <ActivePregnancyPrescribingAlert patientId={patientId} />
      <Modal
        isOpen={isOpiateAlertOpen}
        onClose={() => setIsOpiateAlertOpen(false)}
        title="ePrescribing is not available"
        size="small"
      >
        <div className="opiate-alert-content">
          <p>
            Opiate Substitution Treatment and opiate-family prescriptions cannot be generated through
            ePrescription transfer.
          </p>
          <p>
            Complete these prescriptions on the appropriate prescription pad. They can then be scanned
            and sent to the pharmacy using Healthmail.
          </p>
          {opiateAlertMedicationNames.length > 0 && (
            <>
              <p className="opiate-alert-medication-heading">Flagged medications:</p>
              <ul className="opiate-alert-medication-list">
                {opiateAlertMedicationNames.map((medicationName) => (
                  <li key={medicationName}>{medicationName}</li>
                ))}
              </ul>
            </>
          )}
          <div className="opiate-alert-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => setIsOpiateAlertOpen(false)}
            >
              Acknowledge
            </button>
          </div>
        </div>
      </Modal>

      <form onSubmit={handleSubmit} className="prescription-form">
        {/* Patient Info */}
        {currentPatient && (
          <div className="patient-info-section">
            <h4>Patient Information</h4>
            <div className="patient-info-grid">
              <div>
                <strong>Name:</strong> {currentPatient.firstName} {currentPatient.lastName}
              </div>
              <div>
                <strong>Date of Birth:</strong> {new Date(currentPatient.dateOfBirth).toLocaleDateString('en-GB')}
              </div>
              {showAgeInMonths && (
                <div>
                  <strong>Age:</strong> {patientAge}
                </div>
              )}
              {currentPatient.gmsNumber && (
                <div>
                  <strong>GMS Number:</strong> {currentPatient.gmsNumber}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prescription Type Selection */}
        <div className="form-section">
          <h4>Prescription Details</h4>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="prescription-type">Type</label>
              <select
                id="prescription-type"
                value={prescriptionType}
                onChange={(e) => setPrescriptionType(e.target.value as PrescriptionType)}
                required
              >
                <option value="gms">GMS</option>
                <option value="private">Private</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="script-type">Script Type</label>
              <select
                id="script-type"
                value={scriptType}
                onChange={(e) => setScriptType(e.target.value as ScriptType)}
                required
              >
                <option value="acute">Acute</option>
                <option value="repeat">Repeat</option>
              </select>
            </div>
          </div>

          {scriptType === 'repeat' && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="max-repeats">Max Repeats</label>
                <input
                  id="max-repeats"
                  type="number"
                  min="0"
                  value={maxRepeats || ''}
                  onChange={(e) => setMaxRepeats(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="review-date">Review Date</label>
                <input
                  id="review-date"
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Medication Selection */}
        <div className="form-section">
          <div className="medications-section-header">
            <h4>Medications</h4>
            {medications.length > 0 && (
              <span className="medication-count-badge">{medications.length} medication{medications.length !== 1 ? 's' : ''} added</span>
            )}
          </div>
          <div className="medication-search">
            <div className="medication-search-wrapper">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search and add medications (min 3 characters)..."
                className="medication-search-input"
              />
              {medications.length > 0 && (
                <span className="medication-search-hint">You can add more medications</span>
              )}
            </div>
            {searchResults.length > 0 && (
              <div className="medication-search-results">
                {searchResults.slice(0, 10).map((med) => (
                  <div
                    key={med.drugIdPK}
                    className="medication-search-result"
                    onClick={() => handleAddMedication(med)}
                  >
                    <div className="medication-search-result-main">
                      <div className="medication-name">{med.productName}</div>
                      {formularyStatuses[med.drugIdPK] === true && (
                        <span className="formulary-badge formulary-in">Formulary</span>
                      )}
                      {formularyStatuses[med.drugIdPK] === false && (
                        <span className="formulary-badge formulary-out">Not on formulary</span>
                      )}
                    </div>
                    <div className="medication-details">
                      {med.dosageForm} {med.strength && `- ${med.strength}`}
                      {med.gmsReimbursable && <span className="gms-badge">GMS</span>}
                    </div>
                    <div className="medication-add-hint">Click to add</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {medications.length > 0 && (
            <div className="medications-list">
              {medications.map((med) => (
                <div key={med.medicationId} className="medication-item">
                  <div className="medication-item-summary">
                    <div className="medication-item-header">
                      <div className="medication-item-title-row">
                        <strong>{med.name}</strong>
                        {med.referenceDrugId && formularyStatuses[med.referenceDrugId] === true && (
                          <span className="formulary-badge formulary-in">
                            ✓ In practice formulary
                          </span>
                        )}
                        {med.referenceDrugId && formularyStatuses[med.referenceDrugId] === false && (
                          <span className="formulary-badge formulary-out">
                            ⚠ Not in practice formulary
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-link text-danger"
                        onClick={() => handleRemoveMedication(med.medicationId)}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="medication-item-formulary">
                      <AlternativeMedications
                        medication={{
                          name: med.name,
                          referenceDrugId: med.referenceDrugId,
                          atcCodes: [], // Would need to fetch from medication reference
                        }}
                        onSelectAlternative={(alt) => {
                          handleUpdateMedication(med.medicationId, {
                            name: alt.productName,
                            referenceDrugId: alt.drugIdPK,
                          })
                        }}
                        showBadge={false}
                      />
                    </div>
                  </div>
                  <div className="medication-item-fields">
                    <div className="form-group">
                      <label>Dose</label>
                      <input
                        type="text"
                        value={med.dose || ''}
                        onChange={(e) => handleUpdateMedication(med.medicationId, { dose: e.target.value })}
                        placeholder="e.g. 1 tablet"
                      />
                    </div>
                    <div className="form-group">
                      <label>Route</label>
                      <input
                        type="text"
                        value={med.route || ''}
                        onChange={(e) => handleUpdateMedication(med.medicationId, { route: e.target.value })}
                        placeholder="e.g. Oral"
                      />
                    </div>
                    <div className="form-group">
                      <label>Frequency</label>
                      <input
                        type="text"
                        value={med.frequency || ''}
                        onChange={(e) => handleUpdateMedication(med.medicationId, { frequency: e.target.value })}
                        placeholder="e.g. Twice daily"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantity</label>
                      <div className="quantity-input-group">
                        <input
                          type="number"
                          min="1"
                          value={med.quantity.value || ''}
                          onChange={(e) =>
                            handleUpdateMedication(med.medicationId, {
                              quantity: { ...med.quantity, value: parseInt(e.target.value, 10) || 0 },
                            })
                          }
                        />
                        <input
                          type="text"
                          value={med.quantity.unit || ''}
                          onChange={(e) =>
                            handleUpdateMedication(med.medicationId, {
                              quantity: { ...med.quantity, unit: e.target.value },
                            })
                          }
                          placeholder="units"
                        />
                      </div>
                    </div>
                    <div className="form-group full-width">
                      <label>Instructions</label>
                      <input
                        type="text"
                        value={med.instructions || ''}
                        onChange={(e) => handleUpdateMedication(med.medicationId, { instructions: e.target.value })}
                        placeholder="Additional instructions for this medication"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {medications.length > 0 && currentPatient && (
          <div className="form-section">
            <MedicationAIInsightsPanel
              context={buildMedicationOrderContext(
                currentPatient,
                medications,
                allergyCheck,
                interactionCheck,
                dosageValidation,
                formularyAlternativesForAi,
              )}
            />
          </div>
        )}

        {/* Allergy/Interaction Warnings */}
        {(allergyCheck?.hasAllergies || interactionCheck?.hasInteractions) && (
          <div className="form-section">
            <AllergyInteractionWarnings
              allergyCheck={allergyCheck}
              interactionCheck={interactionCheck}
              onOverrideChange={(overrides) => {
                setOverrideWarnings((prev: any) => {
                  // Merge with existing overrides to preserve dosage overrides
                  const merged = {
                    ...prev,
                    allergies: overrides?.allergies || [],
                    interactions: overrides?.interactions || [],
                    // Preserve dosages if they exist
                    dosages: prev?.dosages || [],
                  }
                  // Only set overriddenBy/overriddenAt if we have any overrides
                  if (overrides && (overrides.allergies?.length > 0 || overrides.interactions?.length > 0)) {
                    merged.overriddenBy = ''
                    merged.overriddenAt = new Date().toISOString()
                  }
                  return merged
                })
              }}
            />
          </div>
        )}

        {dosageValidation?.infoMessages && dosageValidation.infoMessages.length > 0 && (
          <div className="form-section dosage-info-messages" role="status">
            <h4>Dosage checks (informational)</h4>
            <ul className="warning-list">
              {dosageValidation.infoMessages.map((msg: { message: string; medication?: string }, idx: number) => (
                <li key={`info-${idx}`} className="warning-item">
                  {msg.medication && <strong>{msg.medication}: </strong>}
                  {msg.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Dosage Warnings */}
        {dosageValidation?.hasWarnings && (
          <div className="form-section">
            <div className="dosage-warnings">
              <h4>⚠️ Dosage Warnings</h4>
              <p className="warning-intro">
                The following dosage warnings have been detected. To proceed, you must provide a reason for overriding
                each warning.
              </p>
              <ul className="warning-list">
                {dosageValidation.warnings.map((warning: any, idx: number) => (
                  <li key={idx} className="warning-item">
                    <div className="warning-item-header">
                      <span className="medication-name">{warning.medication}</span>
                      <span className={`severity-badge severity-${warning.type}`}>{warning.type}</span>
                    </div>
                    <div className="warning-item-details">{warning.message}</div>
                    {warning.range && (
                      <div className="range-info">
                        Recommended range: {warning.range.min !== undefined ? `${warning.range.min}` : 'N/A'} -{' '}
                        {warning.range.max !== undefined ? `${warning.range.max}` : 'N/A'}{' '}
                        {warning.range.unit || ''}
                        {warning.range.indication && ` for ${warning.range.indication}`}
                      </div>
                    )}
                    <div className="override-reason">
                      <label htmlFor={`dosage-override-${idx}`}>Reason for override (required):</label>
                      <textarea
                        id={`dosage-override-${idx}`}
                        value={overrideWarnings?.dosages?.find((d: any) => d.warningId === `warning-${idx}`)?.reason || ''}
                        onChange={(e) => {
                          setOverrideWarnings((prev: any) => {
                            const dosages = prev?.dosages || []
                            const existing = dosages.findIndex((d: any) => d.warningId === `warning-${idx}`)
                            const newDosage = {
                              medicationId: warning.medication,
                              warningId: `warning-${idx}`,
                              reason: e.target.value,
                            }
                            if (existing >= 0) {
                              dosages[existing] = newDosage
                            } else {
                              dosages.push(newDosage)
                            }
                            return {
                              ...prev,
                              dosages,
                              overriddenBy: '', // Will be set by backend
                              overriddenAt: new Date().toISOString(),
                            }
                          })
                        }}
                        placeholder="Explain why you are proceeding despite this dosage warning..."
                        required
                        rows={2}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="form-section">
          <h4>Instructions</h4>
          <div className="form-group full-width">
            <label htmlFor="instructions">General Instructions</label>
            <textarea
              id="instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="General instructions for the prescription"
            />
          </div>
        </div>

        {/* Handwritten Script */}
        <div className="form-section">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isHandwritten}
                onChange={(e) => setIsHandwritten(e.target.checked)}
              />
              Handwritten Script
            </label>
          </div>
          {isHandwritten && (
            <div className="form-group full-width">
              <label htmlFor="handwritten-notes">Handwritten Notes</label>
              <textarea
                id="handwritten-notes"
                value={handwrittenNotes}
                onChange={(e) => setHandwrittenNotes(e.target.value)}
                rows={3}
                placeholder="Notes about the handwritten prescription"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="form-actions">
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving...'
              : isEditing
                ? 'Update Prescription'
                : 'Create Prescription'}
          </button>
        </div>
      </form>
    </div>
  )
}

